/**
 * editor2/modals/PricingEditModal.tsx
 * 料金編集モーダル
 * PricingSection（コンパクト表示）をクリックすると開き、全料金項目を編集できる。
 * 編集は onChange で即時に editing state へ反映され、パネルの保存ボタンで確定する（従来と同じ）。
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import type { EditorReservation } from '../types';
import InlineEditField from '../shared/InlineEditField';

interface PricingEditModalProps {
  open: boolean;
  data: EditorReservation;
  onChange?: (updates: Record<string, any>) => void;
  disabled?: boolean;
  onClose: () => void;
  onRecalcClick?: () => void;
}

export default function PricingEditModal({
  open,
  data,
  onChange,
  disabled,
  onClose,
  onRecalcClick,
}: PricingEditModalProps) {
  const toggleStyle = (active: boolean): React.CSSProperties => ({
    padding: '2px 8px',
    fontSize: '0.75rem',
    fontWeight: active ? 700 : 400,
    border: active ? '1px solid #3b82f6' : '1px solid #cbd5e1',
    borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer',
    background: active ? '#3b82f6' : '#e2e8f0',
    color: active ? '#ffffff' : '#64748b',
    boxShadow: 'none',
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>料金を編集</DialogTitle>
      <DialogContent dividers>
        <InlineEditField
          label="基本料金"
          field="base_price"
          value={data.base_price}
          onChange={onChange}
          disabled={disabled}
          type="number"
          suffix="円"
        />
        <InlineEditField
          label="オプション料金"
          field="option_price"
          value={data.option_price}
          onChange={onChange}
          disabled={disabled}
          type="number"
          suffix="円"
        />
        <InlineEditField
          label="オプション内容"
          field="option"
          value={data.option}
          onChange={onChange}
          disabled={disabled}
          type="textarea"
        />

        {/* NOC */}
        <div className="gc-field">
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>NOC</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              style={toggleStyle(!!data.noc)}
              onClick={() => !disabled && onChange?.({ noc: !data.noc })}
            >
              NOC
            </button>
            <InlineEditField
              label=""
              field="noc_price"
              value={data.noc_price}
              onChange={onChange}
              disabled={disabled}
              type="number"
              suffix="円"
            />
          </div>
        </div>

        {/* CDW */}
        <div className="gc-field">
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>CDW</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              style={toggleStyle(!!data.cdw)}
              onClick={() => !disabled && onChange?.({ cdw: !data.cdw })}
            >
              CDW
            </button>
            <InlineEditField
              label=""
              field="cdw_price"
              value={data.cdw_price}
              onChange={onChange}
              disabled={disabled}
              type="number"
              suffix="円"
            />
          </div>
        </div>

        <InlineEditField
          label="クーポン"
          field="coupon"
          value={data.coupon}
          onChange={onChange}
          disabled={disabled}
        />
        <InlineEditField
          label="ポイント"
          field="point"
          value={data.point}
          onChange={onChange}
          disabled={disabled}
          type="number"
          suffix="pt"
        />

        <div className="gc-field" style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <InlineEditField
              label="泊数"
              field="nights"
              value={data.nights}
              onChange={onChange}
              disabled={disabled}
              type="number"
              suffix="泊"
            />
          </div>
          <div style={{ flex: 1 }}>
            <InlineEditField
              label="日数"
              field="days"
              value={data.days}
              onChange={onChange}
              disabled={disabled}
              type="number"
              suffix="日"
            />
          </div>
        </div>

        <InlineEditField
          label="合計金額"
          field="sum_price"
          value={data.sum_price}
          onChange={onChange}
          disabled={disabled}
          type="number"
          suffix="円"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {onRecalcClick && (
          <Button
            onClick={onRecalcClick}
            disabled={disabled}
            variant="outlined"
            size="small"
            sx={{ mr: 'auto' }}
          >
            再計算
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
}
